import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  NoticiaEditor,
  type NoticiaEditorSubmit,
} from '@/components/noticias/noticia-editor'
import {
  getNoticia,
  updateNoticia,
  uploadNoticiaImagen,
  type Noticia,
  type UpdateNoticiaBody,
} from '@/features/noticias/api'
import type { NoticiaFormValues } from '@/features/noticias/schema'

export function NoticiaEditPage(): ReactNode {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const {
    data: noticia,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['noticias', id],
    queryFn: () => getNoticia(id),
    enabled: Boolean(id),
  })

  const initialValues = useMemo<NoticiaFormValues | null>(() => {
    if (!noticia) return null
    return {
      titulo: noticia.titulo,
      descripcion: noticia.descripcion,
      ctaTexto: noticia.ctaTexto,
      ctaUrl: noticia.ctaUrl,
      fechaPublicacion: noticia.fechaPublicacion,
      featured: noticia.featured,
      estado: noticia.estado,
    }
  }, [noticia])

  const updateMutation = useMutation({
    mutationFn: async ({
      values,
      imageFile,
    }: NoticiaEditorSubmit): Promise<Noticia> => {
      const body: UpdateNoticiaBody = {
        titulo: values.titulo.trim(),
        descripcion: values.descripcion.trim(),
        ctaTexto: values.ctaTexto.trim(),
        ctaUrl: values.ctaUrl.trim(),
        fechaPublicacion: values.fechaPublicacion,
        featured: values.featured,
        estado: values.estado,
      }
      let updated = await updateNoticia(id, body)
      if (imageFile) {
        try {
          updated = await uploadNoticiaImagen(id, imageFile)
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'No pudimos subir la imagen.'
          toast.error(`Noticia actualizada, pero falló la imagen: ${msg}`)
          return updated
        }
      }
      return updated
    },
    onSuccess: (updated) => {
      toast.success('Noticia actualizada')
      queryClient.setQueryData(['noticias', id], updated)
      void queryClient.invalidateQueries({ queryKey: ['noticias', 'list'] })
      navigate('/dashboard/noticias')
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'No pudimos guardar los cambios.'
      toast.error(msg)
    },
  })

  async function onSubmit(payload: NoticiaEditorSubmit) {
    setSubmitting(true)
    try {
      await updateMutation.mutateAsync(payload)
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading || !noticia || !initialValues) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Editar noticia</h1>
        {isError ? (
          <div className="rounded-lg border bg-card p-6">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'No pudimos cargar la noticia.'}
            </p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/dashboard/noticias')}>
                Volver
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando...
          </div>
        )}
      </div>
    )
  }

  return (
    <NoticiaEditor
      title="Editar noticia"
      description="Actualiza los datos o la imagen de la noticia."
      initialValues={initialValues}
      initialImageUrl={noticia.imagenUrl}
      submitLabel="Guardar cambios"
      submitting={submitting}
      onSubmit={onSubmit}
      onCancel={() => navigate('/dashboard/noticias')}
    />
  )
}
